import { Link } from 'react-router-dom';
import { Fill, LegalPage } from './LegalPage.jsx';

/**
 * /privacy — Privacy Policy.
 *
 * TEMPLATE, drafted against the GDPR because the user base is likely EU-heavy.
 * The processor list below reflects what the backend actually uses today
 * (Resend for email, NOWPayments for payments, DigitalOcean for hosting,
 * OpenAI for forecast generation, Telegram for the bot). Keep it in step with
 * reality — an inaccurate processor list is worse than none.
 */
export function Privacy() {
  return (
    <LegalPage title="Privacy Policy" updated="15 August 2026">
      <p>
        This policy explains what personal data Don Quack collects, why, and
        what rights you have over it. The data controller is{' '}
        <Fill>[Legal entity name]</Fill>, <Fill>[Registered address]</Fill>,
        contactable at <Fill>[privacy email]</Fill>.
      </p>

      <h2>1. What we collect</h2>

      <h3>Information you give us</h3>
      <ul>
        <li>
          <strong>Your email address</strong>, when you create an account. This
          is the only piece of personal information we ask for. We do not
          collect your name, address, date of birth, or phone number.
        </li>
        <li>
          <strong>Messages you send us</strong>, if you contact support.
        </li>
      </ul>

      <h3>Information created by using the service</h3>
      <ul>
        <li>
          <strong>Sign-in records</strong> — hashed one-time codes, when they
          were issued and used, and the number of failed attempts. We store
          codes only in hashed form, never in plain text.
        </li>
        <li>
          <strong>Your IP address</strong>, recorded in server logs and used to
          rate-limit sign-in requests. This is a security measure against
          brute-force and abuse.
        </li>
        <li>
          <strong>Your forecast history</strong> — which fixtures you requested
          and what we returned, so you can see your history in the app.
        </li>
        <li>
          <strong>Subscription and payment records</strong> — your plan, its
          status and dates, the amount, and the payment identifiers returned by
          our payment provider.
        </li>
        <li>
          <strong>Language preference.</strong>
        </li>
      </ul>

      <h3>What we deliberately do not collect</h3>
      <p>
        We do not use analytics, advertising, tracking pixels, or third-party
        tracking cookies of any kind. We do not build advertising profiles, and{' '}
        <strong>we never sell your personal data</strong>.
      </p>
      <p>
        Our fonts are served from our own servers rather than a font CDN, so
        loading a page on Don Quack does not reveal your IP address to any
        third party.
      </p>
      <p>
        We also never see your payment credentials. Cryptocurrency payments are
        handled entirely by NOWPayments; no card number, wallet key, or seed
        phrase ever reaches our servers.
      </p>

      <h2>2. Cookies and local storage</h2>
      <p>
        Don Quack does not set advertising or analytics cookies, so there is no
        consent banner to click through.
      </p>
      <p>
        We do store one item in your browser&rsquo;s local storage: the session
        token that keeps you signed in for up to 30 days. It is strictly
        necessary to operate the service you asked for. Clearing your browser
        storage signs you out.
      </p>

      <h2>3. Why we use your data, and our legal basis</h2>
      <ul>
        <li>
          <strong>To run your account and deliver forecasts</strong> — necessary
          for performance of our contract with you (GDPR Art. 6(1)(b)).
        </li>
        <li>
          <strong>To send one-time sign-in codes</strong> — performance of the
          contract (Art. 6(1)(b)).
        </li>
        <li>
          <strong>To take payment and manage subscriptions</strong> —
          performance of the contract (Art. 6(1)(b)).
        </li>
        <li>
          <strong>To send service emails</strong>, such as a notice before your
          subscription ends — performance of the contract, or our legitimate
          interest in keeping you informed (Art. 6(1)(b) and 6(1)(f)).
        </li>
        <li>
          <strong>To rate-limit sign-ins and prevent abuse</strong> — our
          legitimate interest in keeping the service secure (Art. 6(1)(f)).
        </li>
        <li>
          <strong>To meet tax and accounting obligations</strong> — compliance
          with a legal obligation (Art. 6(1)(c)).
        </li>
      </ul>
      <p>
        We do not use your data for automated decision-making that produces
        legal or similarly significant effects for you. The forecasts themselves
        are generated from sports data, not from anything about you.
      </p>

      <h2>4. Who we share it with</h2>
      <p>
        We share the minimum necessary with service providers who process data
        on our behalf under contract:
      </p>
      <ul>
        <li>
          <strong>Resend</strong> — sends your sign-in codes and service emails.
          Receives your email address.
        </li>
        <li>
          <strong>NOWPayments</strong> — processes cryptocurrency payments.
          Receives an order reference and amount.
        </li>
        <li>
          <strong>DigitalOcean</strong> — hosts our servers and database in{' '}
          <Fill>[data centre region]</Fill>.
        </li>
        <li>
          <strong>OpenAI</strong> — generates forecast commentary. Receives
          match data only; <strong>no personal data about you is sent</strong>.
        </li>
        <li>
          <strong>Telegram</strong> — only if you choose to use our Telegram
          bot, in which case Telegram&rsquo;s own privacy policy also applies.
        </li>
      </ul>
      <p>
        We may also disclose data where legally required, or to establish or
        defend legal claims.
      </p>

      <h2>5. International transfers</h2>
      <p>
        Some providers are based outside the European Economic Area, principally
        in the United States. Where data is transferred outside the EEA we rely
        on the European Commission&rsquo;s Standard Contractual Clauses or an
        adequacy decision.{' '}
        <Fill>
          [Confirm the transfer mechanism in place with each processor.]
        </Fill>
      </p>

      <h2>6. How long we keep it</h2>
      <ul>
        <li>
          <strong>Account data</strong> — while your account exists, then
          deleted within <Fill>[30]</Fill> days of you closing it.
        </li>
        <li>
          <strong>One-time codes</strong> — minutes; they expire and are purged
          shortly after use.
        </li>
        <li>
          <strong>Server and rate-limit logs</strong> — <Fill>[90]</Fill> days.
        </li>
        <li>
          <strong>Forecast history</strong> — while your account exists.
        </li>
        <li>
          <strong>Payment and invoice records</strong> — retained as long as tax
          law requires, typically <Fill>[7]</Fill> years, even after your
          account is closed.
        </li>
      </ul>

      <h2>7. Your rights</h2>
      <p>
        If you are in the EEA or UK you have the right to access your data, to
        have it corrected, to have it erased, to restrict or object to how we
        use it, to receive it in a portable format, and to withdraw consent
        where we rely on it. Exercising these rights is free and we will respond
        within one month.
      </p>
      <p>
        Write to <Fill>[privacy email]</Fill> from the address on your account.
        You also have the right to complain to your national data protection
        authority; ours is <Fill>[Supervisory authority]</Fill>.
      </p>

      <h2>8. Security</h2>
      <p>
        Traffic is encrypted with TLS. Sign-in codes are stored hashed with a
        server-side secret. Access to production systems is restricted, and our
        internal services are not exposed directly to the internet.
      </p>
      <p>
        No system is perfectly secure. If a breach affects your personal data
        and is likely to result in a high risk to your rights, we will notify
        you and the relevant authority as the GDPR requires.
      </p>

      <h2>9. Children</h2>
      <p>
        Don Quack is for adults aged 18 and over. We do not knowingly collect
        data from children. If you believe a child has given us personal data,
        contact <Fill>[privacy email]</Fill> and we will delete it.
      </p>

      <h2>10. Changes</h2>
      <p>
        We will update this policy as the service changes, and will revise the
        date at the top. If a change materially affects your rights we will tell
        you by email.
      </p>

      <p>
        See also our{' '}
        <Link className="link" to="/terms">
          Terms of Service
        </Link>
        .
      </p>
    </LegalPage>
  );
}
